import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Heart, Compass, BookOpen, Video, MessageCircle, Send, ShieldAlert, PenLine, Upload, Youtube } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import useDirtyState from "@/hooks/useDirtyState";
import StickySaveBar from "@/components/StickySaveBar";

/**
 * My Digital Identity — Profile Ext editor.
 *
 * Wires to GET/PUT /me/profile-ext (10 fields). Each field renders on the
 * doctor's public page only when set — so we display a live "will appear on
 * your public page" hint under saved values.
 */
const DEFAULTS = {
    practice_philosophy: "",
    mission_vision: "",
    patient_resources: "",
    teleconsultation_link: "",
    telegram_channel_link: "",
    virtual_clinic_tour_url: "",
    nominee_name: "",
    nominee_relation: "",
    nominee_contact: "",
    nominee_policy_accepted: false,
    signature_image_url: "",
    signature_statement: "",
    signature_signed_at: "",
    intro_video_url: "",
};

const NOMINEE_POLICY = `In the event of my incapacity or passing, my nominee is authorised to
request read-only access to my Doctor Digital Identity, including request-to-archive
or memorial-conversion of my public profile. Access is granted only after identity
verification by DDIP's super admin, per our documented Nominee Access Policy.`;

export default function DoctorProfileExt() {
    const [form, setForm] = useState(DEFAULTS);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const { isDirty, markSaved } = useDirtyState(form);

    useEffect(() => {
        api.get("/me/profile-ext")
            .then((r) => {
                const next = { ...DEFAULTS, ...r.data };
                setForm(next);
                markSaved(next);
            })
            .catch(() => {})
            .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const upd = (k) => (e) => setForm({ ...form, [k]: e && e.target ? e.target.value : e });

    async function uploadSignature(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("category", "signature");
            fd.append("file", file);
            const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            // Backend auto-sets signature_image_url on the doctors row.
            const nextUrl = r.data?.url || "";
            const g = await api.get("/me/profile-ext");
            setForm((f) => ({ ...f, ...g.data, signature_image_url: nextUrl || g.data?.signature_image_url || "" }));
            toast.success("Signature image uploaded.");
        } catch (err) {
            toast.error(errMsg(err, "Upload failed"));
        } finally {
            setSaving(false);
            e.target.value = "";
        }
    }

    async function save(section) {
        setSaving(true);
        try {
            // PUT the whole known-shape — backend merges only provided fields.
            const patch = section === "all"
                ? form
                : Object.fromEntries(section.map((k) => [k, form[k]]));
            await api.put("/me/profile-ext", patch);
            toast.success("Saved — changes will appear on your public page.");
            markSaved(form);
        } catch (e) {
            toast.error(errMsg(e, "Save failed"));
        } finally {
            setSaving(false);
        }
    }

    async function saveAll() {
        return save("all");
    }

    if (!loaded) return <DoctorShell><div className="text-[#17212B]/60">Loading…</div></DoctorShell>;

    return (
        <DoctorShell>
            <div className="mb-6" data-testid="profile-ext-header">
                <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">My Digital Identity</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Practice narrative, quick actions, and nominee</h1>
                <p className="text-sm text-[#17212B]/60 mt-1">
                    Each field below renders on your public profile only when filled.
                    Empty fields are silently skipped — no empty placeholders shown to patients.
                </p>
            </div>

            {/* Narrative */}
            <Card className="border-[#123B5D]/10 mb-4" data-testid="pext-narrative-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Heart size={16} className="text-[#0F766E]" /> Practice narrative
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div>
                        <Label htmlFor="pp">Practice philosophy</Label>
                        <Textarea id="pp" rows={4} value={form.practice_philosophy} onChange={upd("practice_philosophy")} placeholder="Evidence, empathy, time — in that order." data-testid="pext-philosophy" />
                        <p className="text-xs text-[#17212B]/50 mt-1">Renders as the first narrative section on your public page (up to 3000 chars).</p>
                    </div>
                    <div>
                        <Label htmlFor="mv">Mission &amp; vision</Label>
                        <Textarea id="mv" rows={4} value={form.mission_vision} onChange={upd("mission_vision")} placeholder="Deliver world-class care that respects each patient." data-testid="pext-mission" />
                    </div>
                    <div>
                        <Label htmlFor="pr">Patient resources</Label>
                        <Textarea id="pr" rows={4} value={form.patient_resources} onChange={upd("patient_resources")} placeholder="Free-form list of resources you point patients to." data-testid="pext-resources" />
                    </div>
                    <Button onClick={() => save(["practice_philosophy", "mission_vision", "patient_resources"])} disabled={saving} data-testid="pext-save-narrative">
                        <Save size={13} /> {saving ? "Saving…" : "Save narrative"}
                    </Button>
                </CardContent>
            </Card>

            {/* Quick actions */}
            <Card className="border-[#123B5D]/10 mb-4" data-testid="pext-actions-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Send size={16} className="text-[#0F766E]" /> Quick action links
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div>
                        <Label htmlFor="tele">Teleconsultation link</Label>
                        <Input id="tele" type="url" value={form.teleconsultation_link} onChange={upd("teleconsultation_link")} placeholder="https://your-tele-provider/…" data-testid="pext-tele" />
                    </div>
                    <div>
                        <Label htmlFor="tg">Telegram channel link</Label>
                        <Input id="tg" type="url" value={form.telegram_channel_link} onChange={upd("telegram_channel_link")} placeholder="https://t.me/yourhandle" data-testid="pext-telegram" />
                    </div>
                    <div>
                        <Label htmlFor="tour">Virtual clinic tour URL</Label>
                        <Input id="tour" type="url" value={form.virtual_clinic_tour_url} onChange={upd("virtual_clinic_tour_url")} placeholder="YouTube/Vimeo URL — auto-embeds if recognized" data-testid="pext-tour" />
                    </div>
                    <Button onClick={() => save(["teleconsultation_link", "telegram_channel_link", "virtual_clinic_tour_url"])} disabled={saving} data-testid="pext-save-actions">
                        <Save size={13} /> Save action links
                    </Button>
                </CardContent>
            </Card>

            {/* Nominee */}
            <Card className="border-[#123B5D]/10 mb-4" data-testid="pext-nominee-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <ShieldAlert size={16} className="text-[#D97706]" /> Nominee access
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="bg-[#F5F8FA] border border-[#123B5D]/10 rounded-md p-3 text-xs text-[#17212B]/80 leading-relaxed" data-testid="nominee-policy-text">
                        {NOMINEE_POLICY}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <Label htmlFor="nn">Nominee name</Label>
                            <Input id="nn" value={form.nominee_name} onChange={upd("nominee_name")} data-testid="pext-nominee-name" />
                        </div>
                        <div>
                            <Label htmlFor="nr">Relation</Label>
                            <Input id="nr" value={form.nominee_relation} onChange={upd("nominee_relation")} placeholder="Spouse / Sibling / Colleague" data-testid="pext-nominee-relation" />
                        </div>
                        <div>
                            <Label htmlFor="nc">Contact</Label>
                            <Input id="nc" value={form.nominee_contact} onChange={upd("nominee_contact")} placeholder="Phone or email" data-testid="pext-nominee-contact" />
                        </div>
                    </div>
                    <label className="flex items-start gap-2 text-sm text-[#17212B]/80">
                        <Checkbox
                            checked={!!form.nominee_policy_accepted}
                            onCheckedChange={(v) => setForm({ ...form, nominee_policy_accepted: !!v })}
                            data-testid="pext-nominee-policy"
                            className="mt-0.5"
                        />
                        <span>I accept the Nominee Access Policy above. My nominee&apos;s contact will remain private (not shown on my public page) until verification is triggered.</span>
                    </label>
                    <Button onClick={() => save(["nominee_name", "nominee_relation", "nominee_contact", "nominee_policy_accepted"])} disabled={saving} data-testid="pext-save-nominee">
                        <Save size={13} /> Save nominee
                    </Button>
                </CardContent>
            </Card>

            {/* Digital Signature Profile (Phase 3c) */}
            <Card className="border-[#123B5D]/10 mb-4" data-testid="pext-intro-video-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Youtube size={16} className="text-[#DC2626]" /> Hero intro video
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-xs text-[#17212B]/60">
                        Optional. Paste a link to a short intro video — <b>YouTube link recommended</b> (public or unlisted).
                        A &quot;Watch intro&quot; button appears on your hero photo; the video plays in a modal without leaving your page.
                    </p>
                    <div>
                        <Label htmlFor="intro-video">Video URL</Label>
                        <input
                            id="intro-video"
                            type="url"
                            value={form.intro_video_url || ""}
                            onChange={upd("intro_video_url")}
                            placeholder="https://youtu.be/…"
                            className="w-full px-3 py-2 text-sm border rounded-md mt-1"
                            data-testid="pext-intro-video-url"
                        />
                    </div>
                    <Button onClick={() => save(["intro_video_url"])} disabled={saving} data-testid="pext-save-intro-video">
                        <Save size={13} /> Save intro video
                    </Button>
                </CardContent>
            </Card>

            {/* Digital Signature Profile (Phase 3c) */}
            <Card className="border-[#123B5D]/10 mb-4" data-testid="pext-signature-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <PenLine size={16} className="text-[#0F766E]" /> Digital Signature Profile
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-xs text-[#17212B]/60">
                        A short professional statement plus your handwritten signature (PNG with transparent background works best).
                        Both parts are required for the signature block to appear on your public page. Once both are saved, the
                        signed-on date is stamped by the server and preserved unchanged until you re-sign.
                    </p>

                    <div>
                        <Label htmlFor="sig-img">Signature image</Label>
                        <div className="flex items-center gap-3 mt-1">
                            <input
                                id="sig-img"
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={uploadSignature}
                                className="text-sm"
                                data-testid="pext-signature-upload"
                                disabled={saving}
                            />
                            {form.signature_image_url ? (
                                <img
                                    src={form.signature_image_url}
                                    alt="Signature preview"
                                    className="h-12 max-w-[220px] object-contain border border-[#123B5D]/10 rounded bg-white p-1"
                                    data-testid="pext-signature-preview"
                                />
                            ) : (
                                <span className="text-xs text-[#17212B]/50 inline-flex items-center gap-1">
                                    <Upload size={12} /> No signature uploaded yet
                                </span>
                            )}
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="sig-stmt">Signature statement</Label>
                        <Textarea
                            id="sig-stmt"
                            rows={3}
                            maxLength={500}
                            value={form.signature_statement}
                            onChange={upd("signature_statement")}
                            placeholder="I stand by my professional identity and pledge to practise ethical, evidence-based care."
                            data-testid="pext-signature-statement"
                        />
                        <p className="text-xs text-[#17212B]/50 mt-1">Up to 500 characters. This text appears next to your signature on the public page.</p>
                    </div>

                    {form.signature_signed_at ? (
                        <div className="text-xs text-[#0F766E] font-semibold" data-testid="pext-signature-signed-at">
                            Signed on {form.signature_signed_at.split("T")[0]}
                        </div>
                    ) : (
                        <div className="text-xs text-[#D97706]">Not yet signed — save a statement AND upload an image to activate the block.</div>
                    )}

                    <Button
                        onClick={() => save(["signature_statement"])}
                        disabled={saving || !form.signature_statement}
                        data-testid="pext-save-signature"
                    >
                        <Save size={13} /> Save statement
                    </Button>
                </CardContent>
            </Card>
            <StickySaveBar
                isDirty={isDirty}
                saving={saving}
                onSave={saveAll}
                saveLabel="Save all changes"
                testId="pext-sticky-bar"
            />
        </DoctorShell>
    );
}
